import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { formatMoney } from "../../lib/currency";

const label = (m) => {
  if (!/^\d{4}-\d{2}$/.test(m)) return "No date";
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
};

export default function CostTrend({ months = [], title = "Monthly team cost", testid = "cost-trend" }) {
  const rows = months.map((r, i) => {
    const prev = i > 0 ? months[i - 1].cost : null;
    const change = prev ? Math.round((r.cost - prev) / prev * 100) : null;
    return { ...r, name: label(r.month), change, jump: change !== null && change >= 50 };
  });
  const peak = rows.reduce((a, r) => (r.cost > (a?.cost || 0) ? r : a), null);
  return (
    <div className="panel p-4 space-y-3" data-testid={testid}>
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="text-[10px] font-mono uppercase text-neutral-500">{title}</div>
        {peak && <div className="text-[11px] font-mono text-neutral-600" data-testid={`${testid}-peak`}>Highest: {peak.name} · {formatMoney(peak.cost, "INR")}</div>}
      </div>
      {rows.length === 0 ? <div className="text-sm text-neutral-500 py-6">No logged hours yet, so there's no monthly cost to show.</div> : (
        <>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} width={64} tickFormatter={(v) => `₹${v >= 100000 ? `${Math.round(v / 1000) / 100}L` : v >= 1000 ? `${Math.round(v / 100) / 10}k` : v}`} />
                <Tooltip formatter={(v, k) => k === "cost" ? [formatMoney(v, "INR"), "Team cost"] : [v, k]} labelFormatter={(l, p) => `${l}${p?.[0]?.payload?.hours != null ? ` · ${p[0].payload.hours}h` : ""}`} />
                <Bar dataKey="cost" radius={[4, 4, 0, 0]}>
                  {rows.map((r, i) => <Cell key={i} fill={r.jump ? "#E11D48" : "#F97316"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[360px]">
              <thead><tr className="text-[10px] font-mono uppercase text-neutral-500 text-left"><th className="p-2">Month</th><th className="p-2 text-right">Hours</th><th className="p-2 text-right">Cost</th><th className="p-2 text-right">vs last month</th></tr></thead>
              <tbody className="divide-y divide-[#E4E7EC]">
                {rows.map(r => (
                  <tr key={r.month} data-testid={`${testid}-row-${r.month}`}>
                    <td className="p-2">{r.name}</td>
                    <td className="p-2 text-right font-mono">{r.hours}</td>
                    <td className="p-2 text-right font-mono font-semibold">{formatMoney(r.cost, "INR")}</td>
                    <td className={`p-2 text-right font-mono ${r.jump ? "text-rose-600 font-semibold" : r.change < 0 ? "text-emerald-700" : "text-neutral-600"}`}>
                      {r.change === null ? "—" : `${r.change > 0 ? "+" : ""}${r.change}%`}{r.jump && " jump"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[10px] font-mono text-neutral-500">Red bars = cost up 50% or more on the previous month. Work-log hours count in the month they were logged; task hours without a work log count in the month the task was last updated.</div>
        </>
      )}
    </div>
  );
}
